import { useEffect, useState } from "react";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { paymentLabel, REFUND_LABEL, refundState } from "@/lib/merchOrderStatus";

type Ev = { id: string; topic: string; shopify_resource_id: string | null; status: string; result_code: string | null; note: string | null; received_at: string };
type Line = { commission_base: number; commission_amount: number; refunded_base: number; athlete_id: string | null };
type Order = { id: string; shopify_order_id: string; shopify_order_name: string | null; financial_status: string; shopify_financial_status: string | null; paid_at: string | null; order_status: string; is_test: boolean; merch_subtotal_after_discounts: number; created_at: string; merch_order_lines: Line[] };
type Review = { id: string; shopify_line_item_id: string; shopify_product_id: string; product_title_snapshot: string | null };

const cents = (n: number | string) => Math.round(Number(n) * 100);
const usd = (c: number) => `$${(c / 100).toFixed(2)}`;

/** Admin-only Her Collection order sync status and reconciliation. Reads are RLS-limited to admins. */
const AdminOrders = () => {
  const [events, setEvents] = useState<Ev[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [review, setReview] = useState<Review[]>([]);
  const [orderId, setOrderId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string>("");

  const load = async () => {
    const [e, o, r] = await Promise.all([
      supabase.from("shopify_webhook_events").select("id,topic,shopify_resource_id,status,result_code,note,received_at").order("received_at", { ascending: false }).limit(50),
      supabase.from("merch_orders").select("id,shopify_order_id,shopify_order_name,financial_status,shopify_financial_status,paid_at,order_status,is_test,merch_subtotal_after_discounts,created_at,merch_order_lines(commission_base,commission_amount,refunded_base,athlete_id)").order("created_at", { ascending: false }).limit(50),
      supabase.from("merch_order_lines").select("id,shopify_line_item_id,shopify_product_id,product_title_snapshot").eq("attribution_status", "unattributed_needs_review").limit(50),
    ]);
    setEvents((e.data ?? []) as Ev[]);
    setOrders((o.data ?? []) as unknown as Order[]);
    setReview((r.data ?? []) as Review[]);
  };
  useEffect(() => { load(); }, []);

  const reconcile = async (body: Record<string, unknown>) => {
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("shopify-reconcile", { body });
    setBusy(false);
    if (error) {
      const msg = (await (error as any).context?.json?.().catch(() => null))?.message ?? error.message;
      setResult(msg);
      return toast.error(msg);
    }
    const n = data?.results?.length ?? 0;
    setResult(JSON.stringify(data?.results ?? [], null, 2));
    toast.success(`Reconciled ${n} order${n === 1 ? "" : "s"}. Nothing is ever credited twice.`);
    load();
  };

  const cell = "px-2 py-1.5 text-left align-top";
  return (
    <AdminLayout>
      <div className="max-w-5xl space-y-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Her Collection</p>
          <h1 className="mt-2 font-display text-3xl">Order sync</h1>
          <p className="mt-2 text-sm text-muted-foreground">Verified Shopify order notices, recorded orders and items needing review. Test orders never create athlete earnings. Checkout is not open.</p>
        </div>

        <section className="space-y-3 rounded-lg border border-border bg-card p-5 shadow-card">
          <h2 className="font-semibold">Reconcile with Shopify</h2>
          <p className="text-sm text-muted-foreground">Re-reads orders from Shopify and fills in anything missed. Safe to repeat.</p>
          <div className="flex flex-wrap gap-2">
            <input aria-label="Shopify order ID" value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="Shopify order ID (numbers)" className="min-w-0 flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm" />
            <Button disabled={busy || !orderId.trim()} onClick={() => reconcile({ action: "order", shopify_order_id: orderId.trim() })}>Reconcile order</Button>
            <Button variant="outline" disabled={busy} onClick={() => reconcile({ action: "recent", days: 7 })}>Last 7 days</Button>
            <Button variant="outline" disabled={busy} onClick={() => reconcile({ action: "retry_failed" })}>Retry failed notices</Button>
          </div>
          {result && <pre className="max-h-64 overflow-auto rounded bg-muted p-3 text-xs">{result}</pre>}
        </section>

        <section>
          <h2 className="mb-2 font-semibold">Items needing review ({review.length})</h2>
          {review.length === 0 ? <p className="text-sm text-muted-foreground">None.</p> : (
            <ul className="space-y-1 text-sm">{review.map((r) => <li key={r.id}>{r.product_title_snapshot ?? "Untitled"} — product {r.shopify_product_id}, item {r.shopify_line_item_id}</li>)}</ul>
          )}
        </section>

        <section className="overflow-x-auto">
          <h2 className="mb-2 font-semibold">Orders</h2>
          {orders.length === 0 ? <p className="text-sm text-muted-foreground">No orders recorded yet.</p> : (
            <table className="w-full text-sm"><thead><tr className="border-b border-border"><th className={cell}>Order</th><th className={cell}>Payment</th><th className={cell}>Refund</th><th className={cell}>Merch subtotal</th><th className={cell}>Merch refunded</th><th className={cell}>Remaining calc. commission</th><th className={cell}>Type</th></tr></thead>
              <tbody>{orders.map((o) => {
                const lines = o.merch_order_lines ?? [];
                const total = lines.reduce((s, l) => s + cents(l.commission_base), 0);
                const refunded = lines.reduce((s, l) => s + cents(l.refunded_base), 0);
                const remaining = lines.filter((l) => l.athlete_id).reduce((s, l) => {
                  const b = cents(l.commission_base), c = cents(l.commission_amount);
                  return s + (b ? c - Math.round((c * Math.min(cents(l.refunded_base), b)) / b) : 0);
                }, 0);
                const rs = refundState({ order_status: o.order_status, shopify_financial_status: o.shopify_financial_status, merch_refunded_cents: refunded, merch_total_cents: total });
                return <tr key={o.id} className="border-b border-border"><td className={cell}>{o.shopify_order_name ?? o.shopify_order_id}</td><td className={cell}>{paymentLabel(o.financial_status, o.paid_at)}</td><td className={cell}>{REFUND_LABEL[rs]}</td><td className={cell}>${Number(o.merch_subtotal_after_discounts).toFixed(2)}</td><td className={cell}>{usd(refunded)}</td><td className={cell}>{usd(remaining)}</td><td className={cell}>{o.is_test ? "TEST — excluded from payouts" : "Real"}</td></tr>;
              })}</tbody></table>
          )}
        </section>

        <section className="overflow-x-auto">
          <h2 className="mb-2 font-semibold">Order notices (kept 90 days)</h2>
          {events.length === 0 ? <p className="text-sm text-muted-foreground">No notices received yet.</p> : (
            <table className="w-full text-sm"><thead><tr className="border-b border-border"><th className={cell}>Received</th><th className={cell}>Topic</th><th className={cell}>Order</th><th className={cell}>Result</th></tr></thead>
              <tbody>{events.map((e) => <tr key={e.id} className="border-b border-border"><td className={cell}>{new Date(e.received_at).toLocaleString()}</td><td className={cell}>{e.topic}</td><td className={cell}>{e.shopify_resource_id}</td><td className={cell}>{e.status}{e.note ? ` — ${e.note}` : ""}</td></tr>)}</tbody></table>
          )}
        </section>
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;
